import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Layout } from '../components/Layout';
import { pluginRegistry } from '../config';
import { createPluginFetch, loadPluginModule } from '../plugins/loader';
import { currentTheme, observeTheme } from '../theme';

/**
 * Hosts a single browser plugin at `/plugins/:id`. The plugin renders into
 * the container element inside the app chrome.
 */
export default function PluginHostPage() {
  const { id } = useParams<{ id: string }>();
  const { getValidToken } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    // Live theme subscriptions opened by the plugin; torn down on unmount
    // regardless of whether the plugin unsubscribed itself.
    const unsubscribers = new Set<() => void>();
    const subscribeTheme = (listener: (theme: 'light' | 'dark') => void) => {
      const unsubscribe = observeTheme(listener);
      unsubscribers.add(unsubscribe);
      return () => {
        unsubscribe();
        unsubscribers.delete(unsubscribe);
      };
    };

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const entry = pluginRegistry.find(p => p.id === id);
        if (!entry) throw new Error(`Unknown plugin: ${id ?? '(none)'}`);
        const pluginFetch = createPluginFetch(entry.base_url, getValidToken);
        const module = await loadPluginModule(pluginFetch);
        if (cancelled || !containerRef.current) return;
        const result = await module.mount({
          baseUrl: entry.base_url,
          container: containerRef.current,
          fetch: pluginFetch,
          theme: currentTheme(),
          subscribeTheme,
        });
        if (typeof result === 'function') cleanup = result as () => void;
        if (!cancelled) setLoading(false);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
      unsubscribers.forEach(u => u());
      unsubscribers.clear();
      if (typeof cleanup === 'function') cleanup();
    };
  }, [id, getValidToken]);

  return (
    <Layout>
      <div className="host">
        {loading && <p className="status">Loading plugin…</p>}
        {error && <p className="status error">Failed to load plugin: {error}</p>}
        <div ref={containerRef} className="host-container" />
      </div>
    </Layout>
  );
}
